import { FC, useState, useMemo } from 'react';
import { AppCard, AppPage } from '../../components/general';
import { BreadcrumbItem } from '../../components/general/AppPage';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { InputText } from 'primereact/inputtext';
import {
  WaterfallMigrationDiff,
  WaterfallMigrationWaterfallDiff,
  WaterfallMigrationVersionDiff,
  WaterfallMigrationImportRequest,
  WaterfallMigrationImportResult,
  WaterfallDiffAction,
} from '../../models/waterfall';

interface WaterfallMigrationReviewProps {
  diff: WaterfallMigrationDiff;
  onImport: (request: WaterfallMigrationImportRequest) => Promise<WaterfallMigrationImportResult>;
  onCancel: () => void;
  breadcrumbs?: BreadcrumbItem[];
}

const actionSeverity = (action: WaterfallDiffAction): 'success' | 'info' | 'danger' => {
  switch (action) {
    case WaterfallDiffAction.Insert: return 'success';
    case WaterfallDiffAction.Update: return 'info';
    case WaterfallDiffAction.Delete: return 'danger';
  }
};

const actionIcon = (action: WaterfallDiffAction): string => {
  switch (action) {
    case WaterfallDiffAction.Insert: return 'pi pi-plus-circle';
    case WaterfallDiffAction.Update: return 'pi pi-pencil';
    case WaterfallDiffAction.Delete: return 'pi pi-trash';
  }
};

interface FlatVersionDiff {
  waterfallIndex: number;
  versionIndex: number;
  waterfallName: string;
  integrationType: string;
  waterfallAction: WaterfallDiffAction;
  versionDiff: WaterfallMigrationVersionDiff;
}

export const WaterfallMigrationReview: FC<WaterfallMigrationReviewProps> = ({
  diff,
  onImport,
  onCancel,
  breadcrumbs,
}) => {
  const defaultBreadcrumbs: BreadcrumbItem[] = [
    { label: 'Dashboard', to: '/dashboard' },
    { label: 'Waterfalls' },
    { label: 'Migration Review' },
  ];

  const flatRows = useMemo(() => {
    const rows: FlatVersionDiff[] = [];
    diff.waterfallDiffs.forEach((wd, wi) => {
      if (wd.versionDiffs.length === 0) {
        rows.push({
          waterfallIndex: wi,
          versionIndex: -1,
          waterfallName: wd.waterfallName,
          integrationType: wd.integrationType,
          waterfallAction: wd.action,
          versionDiff: {
            action: wd.action,
            sourceVersionNumber: 0,
            imported: null,
            current: null,
            changedFields: [],
          },
        });
      } else {
        wd.versionDiffs.forEach((vd, vi) => {
          rows.push({
            waterfallIndex: wi,
            versionIndex: vi,
            waterfallName: wd.waterfallName,
            integrationType: wd.integrationType,
            waterfallAction: wd.action,
            versionDiff: vd,
          });
        });
      }
    });
    return rows;
  }, [diff]);

  const allKeys = useMemo(() => flatRows.map((_, i) => i), [flatRows]);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set(allKeys));
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<WaterfallMigrationImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [importNotes, setImportNotes] = useState('');

  const totalSelected = selectedRows.size;
  const totalDiffs = flatRows.length;

  const toggleRow = (index: number) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const selectAll = () => setSelectedRows(new Set(allKeys));
  const deselectAll = () => setSelectedRows(new Set());

  const handleImport = async () => {
    setShowConfirmDialog(false);
    setIsImporting(true);
    setError(null);

    const selectedWaterfallDiffs: WaterfallMigrationWaterfallDiff[] = [];
    const waterfallMap = new Map<number, WaterfallMigrationWaterfallDiff>();

    flatRows.forEach((row, i) => {
      if (!selectedRows.has(i)) return;
      const origWd = diff.waterfallDiffs[row.waterfallIndex];

      if (!waterfallMap.has(row.waterfallIndex)) {
        waterfallMap.set(row.waterfallIndex, {
          action: origWd.action,
          waterfallName: origWd.waterfallName,
          integrationType: origWd.integrationType,
          imported: origWd.imported,
          current: origWd.current,
          versionDiffs: [],
        });
        selectedWaterfallDiffs.push(waterfallMap.get(row.waterfallIndex)!);
      }

      if (row.versionIndex >= 0) {
        waterfallMap.get(row.waterfallIndex)!.versionDiffs.push(row.versionDiff);
      }
    });

    const request: WaterfallMigrationImportRequest = {
      waterfallDiffs: selectedWaterfallDiffs,
      importNotes: importNotes.trim() || null,
    };

    try {
      const result = await onImport(request);
      setImportResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setIsImporting(false);
    }
  };

  const selectedInserts = flatRows.filter((r, i) => selectedRows.has(i) && r.versionDiff.action === WaterfallDiffAction.Insert).length;
  const selectedUpdates = flatRows.filter((r, i) => selectedRows.has(i) && r.versionDiff.action === WaterfallDiffAction.Update).length;
  const selectedDeletes = flatRows.filter((r, i) => selectedRows.has(i) && r.versionDiff.action === WaterfallDiffAction.Delete).length;

  if (isImporting) {
    return (
      <AppPage title="Migration Review" breadcrumbs={breadcrumbs || defaultBreadcrumbs}>
        <AppCard>
          <div className="flex flex-column align-items-center justify-content-center gap-3 py-6">
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
            <span className="text-lg font-semibold">Importing changes...</span>
            <span className="text-sm text-gray-500">Please do not navigate away from this page.</span>
          </div>
        </AppCard>
      </AppPage>
    );
  }

  if (importResult) {
    return (
      <AppPage title="Migration Review" breadcrumbs={breadcrumbs || defaultBreadcrumbs}>
        <AppCard>
          <div className="flex flex-column gap-4">
            <div className={`p-4 border-round ${importResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
              <div className="flex align-items-center gap-2 mb-3">
                <i className={`pi ${importResult.success ? 'pi-check-circle text-green-600' : 'pi-exclamation-triangle text-red-600'} text-2xl`} />
                <span className="text-lg font-bold">
                  {importResult.success ? 'Import Completed Successfully' : 'Import Completed with Errors'}
                </span>
              </div>
              <div className="grid">
                <div className="col-6 md:col-3">
                  <div className="text-sm text-gray-600">Waterfalls Inserted</div>
                  <div className="text-xl font-bold text-green-700">{importResult.waterfallsInserted}</div>
                </div>
                <div className="col-6 md:col-3">
                  <div className="text-sm text-gray-600">Waterfalls Updated</div>
                  <div className="text-xl font-bold text-blue-700">{importResult.waterfallsUpdated}</div>
                </div>
                <div className="col-6 md:col-3">
                  <div className="text-sm text-gray-600">Versions Inserted</div>
                  <div className="text-xl font-bold text-green-700">{importResult.versionsInserted}</div>
                </div>
              </div>
              {importResult.warnings.length > 0 && (
                <div className="mt-3">
                  <div className="text-sm font-semibold text-yellow-700 mb-1">Warnings:</div>
                  {importResult.warnings.map((w, i) => (
                    <div key={i} className="text-sm text-yellow-600">&#x26A0; {w}</div>
                  ))}
                </div>
              )}
              {importResult.errors.length > 0 && (
                <div className="mt-3">
                  <div className="text-sm font-semibold text-red-700 mb-1">Errors:</div>
                  {importResult.errors.map((err, i) => (
                    <div key={i} className="text-sm text-red-600">&bull; {err}</div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end">
              <Button label="Back to Waterfalls" icon="pi pi-arrow-left" onClick={onCancel} />
            </div>
          </div>
        </AppCard>
      </AppPage>
    );
  }

  if (totalDiffs === 0 && diff.warnings.length === 0) {
    return (
      <AppPage title="Migration Review" breadcrumbs={breadcrumbs || defaultBreadcrumbs}>
        <AppCard>
          <div className="flex flex-column align-items-center gap-3 py-6">
            <i className="pi pi-check-circle text-green-500 text-5xl" />
            <span className="text-lg font-semibold">No differences found</span>
            <span className="text-sm text-gray-500">The imported file matches the current waterfall configuration.</span>
            <Button label="Back to Waterfalls" icon="pi pi-arrow-left" onClick={onCancel} className="mt-2" />
          </div>
        </AppCard>
      </AppPage>
    );
  }

  return (
    <AppPage
      title="Migration Review"
      breadcrumbs={breadcrumbs || defaultBreadcrumbs}
      headerEnd={
        <div className="flex gap-2 align-items-center">
          <span className="text-sm text-gray-600">{totalSelected} of {totalDiffs} selected</span>
          <Button label="Select All" className="p-button-sm p-button-text" onClick={selectAll} />
          <Button label="Deselect All" className="p-button-sm p-button-text" onClick={deselectAll} />
          <Button
            label="Import Selected"
            icon="pi pi-upload"
            className="p-button-sm p-button-primary"
            onClick={() => setShowConfirmDialog(true)}
            disabled={totalSelected === 0}
          />
          <Button
            label="Cancel"
            className="p-button-sm p-button-secondary"
            onClick={onCancel}
          />
        </div>
      }
    >
      {error && (
        <div className="mb-3 p-3 bg-red-50 border-round">
          <div className="flex align-items-center gap-2">
            <i className="pi pi-exclamation-triangle text-red-600" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Warnings from PrepareImport */}
      {diff.warnings.length > 0 && (
        <div className="mb-3 p-3 bg-yellow-50 border-round">
          <div className="flex align-items-start gap-2 mb-2">
            <i className="pi pi-exclamation-triangle text-yellow-600 text-xl mt-1" />
            <div className="font-semibold text-yellow-800">Migration Warnings</div>
          </div>
          {diff.warnings.map((w, i) => (
            <div key={i} className="text-sm text-yellow-700 ml-4">&#x26A0; {w}</div>
          ))}
        </div>
      )}

      {/* Summary bar */}
      <div className="mb-3 p-3 surface-100 border-round flex gap-4 align-items-center flex-wrap">
        <span className="font-semibold text-sm">Summary:</span>
        <Tag severity="success" value={`${selectedInserts} Insert(s)`} icon="pi pi-plus-circle" />
        <Tag severity="info" value={`${selectedUpdates} Update(s)`} icon="pi pi-pencil" />
        <Tag severity="danger" value={`${selectedDeletes} Delete(s)`} icon="pi pi-trash" />
      </div>

      {/* Waterfall Version Diffs */}
      <AppCard>
        <h3 className="mt-0 mb-3">Waterfall Changes ({totalDiffs})</h3>
        <DataTable
          value={flatRows.map((d, i) => ({ ...d, _index: i }))}
          stripedRows
          className="p-datatable-sm p-datatable-gridlines"
          paginator
          rows={10}
          rowsPerPageOptions={[10, 25, 50, 100]}
          emptyMessage="No waterfall changes"
        >
          <Column
            header=""
            style={{ width: '3rem' }}
            body={(row: FlatVersionDiff & { _index: number }) => (
              <Checkbox
                checked={selectedRows.has(row._index)}
                onChange={() => toggleRow(row._index)}
              />
            )}
          />
          <Column
            field="versionDiff.action"
            header="Action"
            style={{ width: '8rem' }}
            body={(row: FlatVersionDiff) => (
              <Tag severity={actionSeverity(row.versionDiff.action)} value={row.versionDiff.action} icon={actionIcon(row.versionDiff.action)} />
            )}
          />
          <Column field="waterfallName" header="Waterfall" />
          <Column field="integrationType" header="Type" style={{ width: '10rem' }} />
          <Column
            header="Version"
            style={{ width: '6rem' }}
            body={(row: FlatVersionDiff) =>
              row.versionDiff.sourceVersionNumber > 0
                ? `v${row.versionDiff.sourceVersionNumber}`
                : '-'
            }
          />
          <Column
            header="Details"
            body={(row: FlatVersionDiff) => {
              if (row.versionDiff.action === WaterfallDiffAction.Insert) {
                const stepCount = row.versionDiff.imported?.initialSteps?.length ?? 0;
                return <span className="text-sm">{stepCount} step(s)</span>;
              }
              if (row.versionDiff.action === WaterfallDiffAction.Update && row.versionDiff.changedFields.length > 0) {
                return row.versionDiff.changedFields.map((f) => (
                  <Tag key={f} value={f} className="mr-1 mb-1" severity="warning" style={{ fontSize: '0.7rem' }} />
                ));
              }
              return '-';
            }}
          />
        </DataTable>
      </AppCard>

      {/* Confirm Dialog */}
      <Dialog
        header="Confirm Import"
        visible={showConfirmDialog}
        onHide={() => setShowConfirmDialog(false)}
        style={{ width: '480px' }}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              label="Cancel"
              className="p-button-secondary"
              onClick={() => setShowConfirmDialog(false)}
            />
            <Button
              label="Yes, Import"
              icon="pi pi-upload"
              className="p-button-danger"
              onClick={handleImport}
            />
          </div>
        }
      >
        <div className="flex flex-column gap-3">
          <div className="bg-yellow-50 p-3 border-round">
            <div className="flex align-items-start gap-2">
              <i className="pi pi-exclamation-triangle text-yellow-600 text-xl mt-1" />
              <div>
                <p className="m-0 font-semibold text-yellow-800">This will modify waterfall configurations.</p>
                <p className="m-0 mt-1 text-sm text-yellow-700">
                  The following changes will be applied:
                </p>
              </div>
            </div>
          </div>
          <div className="surface-100 p-3 border-round">
            {selectedInserts > 0 && <div className="text-sm text-green-700">+ {selectedInserts} insert(s)</div>}
            {selectedUpdates > 0 && <div className="text-sm text-blue-700">~ {selectedUpdates} update(s)</div>}
            {selectedDeletes > 0 && <div className="text-sm text-red-700">- {selectedDeletes} delete(s)</div>}
            {selectedInserts === 0 && selectedUpdates === 0 && selectedDeletes === 0 && (
              <div className="text-sm text-gray-500">No changes</div>
            )}
          </div>
          <div className="flex flex-column gap-2">
            <label className="text-sm font-semibold" htmlFor="importNotes">Import Notes (optional)</label>
            <InputText
              id="importNotes"
              value={importNotes}
              onChange={(e) => setImportNotes(e.target.value)}
              placeholder="e.g., Imported from production 2026-03-28"
              className="w-full"
            />
          </div>
        </div>
      </Dialog>
    </AppPage>
  );
};
